import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { Check, AlertCircle, Circle, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { toast } from "sonner";

// Per-occurrence placeholder with stable anchors
export interface PlaceholderOccurrence {
  id: string;
  placeholderText: string;      // e.g., "[Your Job Title]"
  label: string;                // derived label
  startIndex: number;           // original position in prompt
  contextBefore: string;        // 20 chars before for anchor
  contextAfter: string;         // 20 chars after for anchor
  currentValue: string;
  appliedValue: string;
  isApplied: boolean;
  isExpanded: boolean;
  showFullValue: boolean;       // for "Show more" toggle
}

interface PlaceholderFillPanelProps {
  isOpen: boolean;
  onClose: () => void;
  improvedPrompt: string;
  originalPromptForReset: string; // The original improved prompt before any replacements
  onApplyField: (occurrence: PlaceholderOccurrence, value: string, oldValue?: string) => void;
  onScrollToPlaceholder?: (placeholderId: string) => void;
  fallbackSuggestions?: string[];
}

// Extract ALL occurrences (not deduplicated) with stable anchors
const extractPlaceholderOccurrences = (text: string): PlaceholderOccurrence[] => {
  const regex = /\[[^\]]{2,120}\]/g;
  const occurrences: PlaceholderOccurrence[] = [];
  let match;
  let fieldCounter: Record<string, number> = {};

  while ((match = regex.exec(text)) !== null) {
    const placeholder = match[0];
    // Must contain at least one letter or number
    if (/[a-zA-Z0-9]/.test(placeholder)) {
      const startIndex = match.index;
      const endIndex = startIndex + placeholder.length;
      
      // Extract surrounding context for stable anchoring
      const contextBefore = text.slice(Math.max(0, startIndex - 20), startIndex);
      const contextAfter = text.slice(endIndex, Math.min(text.length, endIndex + 20));
      
      // Track occurrences for label uniqueness
      fieldCounter[placeholder] = (fieldCounter[placeholder] || 0) + 1;
      const occurrenceNum = fieldCounter[placeholder];
      
      occurrences.push({
        id: `placeholder-${startIndex}`,
        placeholderText: placeholder,
        label: generateLabel(placeholder, occurrences.length, occurrenceNum > 1 ? occurrenceNum : undefined),
        startIndex,
        contextBefore,
        contextAfter,
        currentValue: "",
        appliedValue: "",
        isApplied: false,
        isExpanded: occurrences.length === 0, // First one expanded by default
        showFullValue: false,
      });
    }
  }

  return occurrences;
};

// Generate a readable label from placeholder content
const generateLabel = (placeholder: string, index: number, occurrenceNum?: number): string => {
  const content = placeholder.slice(1, -1);
  let label = "";
  
  if (/role|job title/i.test(content)) label = "Role";
  else if (/tenure|years/i.test(content)) label = "Tenure";
  else if (/achievement\s*1/i.test(content)) label = "Achievement 1";
  else if (/achievement\s*2/i.test(content)) label = "Achievement 2";
  else if (/achievement\s*3/i.test(content)) label = "Achievement 3";
  else if (/boss.*style/i.test(content)) label = "Boss's Style";
  else if (/company/i.test(content)) label = "Company";
  else if (/name/i.test(content)) label = "Name";
  else if (/goal/i.test(content)) label = "Goal";
  else if (/deadline/i.test(content)) label = "Deadline";
  else if (/audience/i.test(content)) label = "Audience";
  else if (/tone/i.test(content)) label = "Tone";
  else if (/context/i.test(content)) label = "Context";
  else if (/example/i.test(content)) label = "Example";
  else {
    const yourMatch = content.match(/^Your\s+([^,.:]+)/i);
    if (yourMatch) label = yourMatch[1].trim();
    else {
      const egMatch = content.match(/^([^,]+),?\s*e\.g\./i);
      if (egMatch) label = egMatch[1].trim();
      else label = `Field ${index + 1}`;
    }
  }
  
  // Add occurrence number if this is a repeated placeholder
  if (occurrenceNum) {
    label = `${label} (${occurrenceNum})`;
  }
  
  return label;
};

const PlaceholderFillPanel = ({
  isOpen,
  onClose,
  improvedPrompt,
  originalPromptForReset,
  onApplyField,
  onScrollToPlaceholder,
  fallbackSuggestions,
}: PlaceholderFillPanelProps) => {
  const isMobile = useIsMobile();
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  
  // Store the original detected occurrences (never re-detect after initial open)
  const [initialOccurrences, setInitialOccurrences] = useState<PlaceholderOccurrence[]>([]);
  const [items, setItems] = useState<PlaceholderOccurrence[]>([]);
  const hasInitialized = useRef(false);

  // Initialize occurrences ONLY on first open with the original prompt
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      const detected = extractPlaceholderOccurrences(originalPromptForReset);
      setInitialOccurrences(detected);
      setItems(detected);
      hasInitialized.current = true;
    }
  }, [isOpen, originalPromptForReset]);

  // Reset when panel closes fully
  useEffect(() => {
    if (!isOpen) {
      // Keep the items but mark as uninitialized for next session if needed
      // Actually we want to preserve state across opens
    }
  }, [isOpen]);

  const updateValue = (id: string, value: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, currentValue: value } : item
      )
    );
  };

  const toggleExpanded = (id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isExpanded: !item.isExpanded } : item
      )
    );
  };

  const toggleShowFullValue = (id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, showFullValue: !item.showFullValue } : item
      )
    );
  };

  const findNextPendingIndex = useCallback((currentIndex: number): number => {
    for (let i = currentIndex + 1; i < items.length; i++) {
      if (!items[i].isApplied) return i;
    }
    return -1;
  }, [items]);

  const handleApplyField = (item: PlaceholderOccurrence, itemIndex: number) => {
    if (!item.currentValue.trim()) {
      toast.error("Please enter a value first");
      return;
    }
    
    onApplyField(item, item.currentValue.trim());
    
    // Find next pending item
    const nextPendingIndex = findNextPendingIndex(itemIndex);
    
    setItems((prev) =>
      prev.map((i, idx) => {
        if (i.id === item.id) {
          return { 
            ...i, 
            appliedValue: i.currentValue.trim(), 
            isApplied: true, 
            isExpanded: false 
          };
        }
        // Expand next pending item
        if (nextPendingIndex !== -1 && idx === nextPendingIndex) {
          return { ...i, isExpanded: true };
        }
        return i;
      })
    );
    
    toast.success("Inserted into improved prompt");
    
    // Auto-advance: focus next pending textarea and scroll prompt view
    if (nextPendingIndex !== -1) {
      const nextItem = items[nextPendingIndex];
      setTimeout(() => {
        textareaRefs.current[nextItem.id]?.focus();
        onScrollToPlaceholder?.(nextItem.id);
      }, 100);
    }
  };

  const handleEditField = (id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isExpanded: true, currentValue: item.appliedValue } : item
      )
    );
  };

  const handleUpdateField = (item: PlaceholderOccurrence, itemIndex: number) => {
    if (!item.currentValue.trim()) {
      toast.error("Please enter a value first");
      return;
    }
    
    // Pass the old applied value so parent can find and replace it
    onApplyField(item, item.currentValue.trim(), item.appliedValue);
    
    setItems((prev) =>
      prev.map((i) =>
        i.id === item.id
          ? { ...i, appliedValue: i.currentValue.trim(), isExpanded: false }
          : i
      )
    );
    
    toast.success("Updated in improved prompt");
  };

  const hasPlaceholders = initialOccurrences.length > 0;
  const hasFallback = fallbackSuggestions && fallbackSuggestions.length > 0;
  const completedCount = items.filter(i => i.isApplied).length;
  const totalCount = items.length;

  const truncateValue = (value: string, maxLength: number = 50): string => {
    if (value.length <= maxLength) return value;
    return value.slice(0, maxLength) + "...";
  };

  const content = (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto py-4 space-y-3 px-1">
        {!hasPlaceholders ? (
          <div className="text-center py-6">
            <AlertCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm mb-4">
              No fill-in fields detected in this prompt.
            </p>
            
            {hasFallback && (
              <div className="text-left mt-6">
                <p className="text-sm font-medium text-foreground mb-3">
                  Suggested improvements from coaching feedback:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {fallbackSuggestions!.slice(0, 5).map((suggestion, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Section Header */}
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Context Inputs
                </h4>
                <span className="text-xs text-muted-foreground">
                  {completedCount}/{totalCount} completed
                </span>
              </div>
            </div>

            {/* Checklist Items */}
            {items.map((item, idx) => {
              const isValueChanged = item.isApplied && item.currentValue !== item.appliedValue;
              
              return (
                <div
                  key={item.id}
                  className={`rounded-lg border transition-all ${
                    item.isApplied
                      ? "border-success/30 bg-success/5"
                      : "border-border bg-card"
                  }`}
                >
                  {/* Row Header - Always Visible */}
                  <div 
                    className="flex items-center gap-3 p-3 cursor-pointer"
                    onClick={() => item.isApplied && !item.isExpanded ? handleEditField(item.id) : toggleExpanded(item.id)}
                  >
                    {/* iOS-style circle/check */}
                    <div className="flex-shrink-0">
                      {item.isApplied ? (
                        <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground/50" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-foreground truncate">
                          {item.placeholderText}
                        </span>
                      </div>
                      
                      {/* Collapsed Applied Value */}
                      {item.isApplied && !item.isExpanded && (
                        <div className="mt-1">
                          <p className="text-xs text-muted-foreground">
                            {item.showFullValue ? item.appliedValue : truncateValue(item.appliedValue)}
                            {item.appliedValue.length > 50 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleShowFullValue(item.id); }}
                                className="ml-1 text-primary hover:underline"
                              >
                                {item.showFullValue ? "Show less" : "Show more"}
                              </button>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex-shrink-0 flex items-center gap-1">
                      {item.isApplied && !item.isExpanded && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleEditField(item.id); }}
                          className="h-7 px-2 text-xs"
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      )}
                      {item.isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {item.isExpanded && (
                    <div className="px-3 pb-3 pt-0">
                      <div className="pl-8">
                        <Textarea
                          ref={(el) => { textareaRefs.current[item.id] = el; }}
                          value={item.currentValue}
                          onChange={(e) => updateValue(item.id, e.target.value)}
                          placeholder={`Enter value for ${item.label.toLowerCase()}...`}
                          className="text-sm min-h-[80px] resize-none"
                          rows={3}
                        />
                        
                        <div className="flex justify-end mt-2 gap-2">
                          {item.isApplied ? (
                            <Button
                              size="sm"
                              variant={isValueChanged ? "default" : "outline"}
                              onClick={() => handleUpdateField(item, idx)}
                              disabled={!item.currentValue.trim() || !isValueChanged}
                            >
                              Update
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleApplyField(item, idx)}
                              disabled={!item.currentValue.trim()}
                            >
                              Apply
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
      
      {/* Close button at bottom */}
      <div className="pt-4 border-t border-border flex-shrink-0">
        <Button variant="ghost" onClick={onClose} className="w-full">
          {hasPlaceholders ? "Done" : "Close"}
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-display">
              Fill in the missing details
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 overflow-y-auto flex-1">{content}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display">
            Fill in the missing details
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto">{content}</div>
      </SheetContent>
    </Sheet>
  );
};

export default PlaceholderFillPanel;
